import { useEffect, useMemo, useState } from "react";
import ImageTemplateServices from "../services/ImageTemplateServices";
import { LIVE_YANTRA_IMAGE_URL } from "./utils";

/**
 * Outlet-only yantra-image override shared across live and all-draw pages.
 *
 * The actual override map ({ [yantra_id]: image_filename }) lives in module
 * scope so every call to getYantraImageUrl(img, yantra_id) is an O(1) plain
 * object lookup — no prop drilling, no per-render React work. A tiny pub-sub
 * around a version counter lets mounted screens re-render exactly once when
 * the override is fetched, with no further cost on subsequent renders.
 *
 * Fetch policy: at most one HTTP call per mount of useOutletTemplateImages
 * (per user_id / role change). Admin edits to the template do not stream
 * back to live screens, which is the explicit behavior the product wants.
 */
let TEMPLATE_YANTRA_IMG_OVERRIDE = null;
let templateVersionTick = 0;
const versionListeners = new Set();

const notifyListeners = () => {
  versionListeners.forEach((fn) => {
    try {
      fn(templateVersionTick);
    } catch (_) {}
  });
};

export const setTemplateImageOverride = (map) => {
  if (map && typeof map === "object") {
    const cleaned = {};
    Object.keys(map).forEach((k) => {
      if (map[k]) cleaned[k] = map[k];
    });
    TEMPLATE_YANTRA_IMG_OVERRIDE = Object.keys(cleaned).length ? cleaned : null;
  } else {
    TEMPLATE_YANTRA_IMG_OVERRIDE = null;
  }
  templateVersionTick += 1;
  notifyListeners();
};

export const clearTemplateImageOverride = () => {
  setTemplateImageOverride(null);
};

export const hasTemplateImageOverride = () => TEMPLATE_YANTRA_IMG_OVERRIDE != null;

export const getYantraImageUrl = (img, yantraId) => {
  const map = TEMPLATE_YANTRA_IMG_OVERRIDE;
  if (map && yantraId != null && map[yantraId]) {
    return `${LIVE_YANTRA_IMAGE_URL}/${map[yantraId]}`;
  }
  return img
    ? `${LIVE_YANTRA_IMAGE_URL}/${img}`
    : `${LIVE_YANTRA_IMAGE_URL}/default_image.png`;
};

/**
 * Outlet-only hook. For role_id === 3 it fetches the user's active assigned
 * template once and updates the module-level override. For any other role
 * (or logged-out state) it clears the override. Returns a `templateVersion`
 * number that consumers can include in useMemo deps so memoized URL lists
 * refresh once when the override resolves.
 *
 * Cleanup intentionally does NOT clear the override, so the same outlet user
 * can navigate between Outlet Live Session and All Draw without re-fetching
 * or losing the templated images mid-navigation.
 */
export const useOutletTemplateImages = (user) => {
  const api = useMemo(() => new ImageTemplateServices(), []);
  const [version, setVersion] = useState(templateVersionTick);

  useEffect(() => {
    const handle = (v) => setVersion(v);
    versionListeners.add(handle);
    return () => {
      versionListeners.delete(handle);
    };
  }, []);

  useEffect(() => {
    const isOutlet = user && Number(user.role_id) === 3;
    if (!isOutlet) {
      setTemplateImageOverride(null);
      return undefined;
    }
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getMyActiveImages({ signal: controller.signal });
        if (cancelled) return;
        const payload = res?.data;
        if (
          res?.isSuccess &&
          payload &&
          Array.isArray(payload.images) &&
          payload.images.length > 0
        ) {
          const map = {};
          payload.images.forEach((row) => {
            if (row?.yantra_id != null && row?.image_filename) {
              map[row.yantra_id] = row.image_filename;
            }
          });
          setTemplateImageOverride(map);
        } else {
          setTemplateImageOverride(null);
        }
      } catch (_) {
        if (controller.signal.aborted) return;
        setTemplateImageOverride(null);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [user?.user_id, user?.role_id, api]);

  return version;
};
